import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import { X } from 'Utils/lucideIcons';
import styles from './index.module.css';

/**
 * Two-button confirmation dialog in the dark auth theme.
 *
 * The app-wide <InfoModal> is a light MUI dialog, which reads as a different
 * product on top of the redesigned auth screens — this is its counterpart for
 * that flow: same glass panel, hairline rim and accent gradient as the auth
 * card, plus the keyboard and focus behaviour a dialog owes its user.
 *
 * Rendered into a portal on <body> so the blurred scrim covers the whole
 * viewport: .authPage's own fixed backdrop layer would otherwise clip it.
 *
 *   tone      'accent' (default) or 'danger' — tints the icon and the
 *             confirming button.
 *   icon      Node rendered inside the icon tile; omit for no tile.
 *   isBusy    Keeps the dialog open and locked while the action runs.
 */
export const AuthConfirmModal = ({
  isVisible,
  title,
  message,
  icon,
  tone = 'accent',
  confirmLabel = 'Confirm',
  cancelLabel = 'Go back',
  onConfirm,
  onCancel,
  isBusy = false,
}) => {
  const cardRef = useRef(null);
  const cancelRef = useRef(null);
  // Whatever the user was on before the dialog opened — the control that opened
  // it — so focus can go back there on dismiss.
  const previouslyFocusedRef = useRef(null);

  useEffect(() => {
    if (!isVisible) return undefined;
    previouslyFocusedRef.current = document.activeElement;
    cancelRef.current?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
      // Only restore if focus is still inside (or lost to) the dialog we're
      // tearing down; a logout replaces the page, where this is a no-op.
      if (previouslyFocusedRef.current?.isConnected) {
        previouslyFocusedRef.current.focus();
      }
    };
  }, [isVisible]);

  // Esc dismisses; Tab is trapped so focus can't wander to the locked page
  // behind the scrim.
  useEffect(() => {
    if (!isVisible) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (!isBusy) onCancel();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = cardRef.current?.querySelectorAll(
        'button:not(:disabled), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable || !focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => { document.removeEventListener('keydown', handleKeyDown); };
  }, [isVisible, isBusy, onCancel]);

  if (!isVisible) return null;

  return createPortal(
    <div
      className={styles.overlay}
      role="presentation"
      onMouseDown={(event) => {
        // mousedown rather than click so a drag that starts inside the panel
        // and ends on the scrim doesn't dismiss it.
        if (event.target === event.currentTarget && !isBusy) onCancel();
      }}
    >
      <div
        ref={cardRef}
        className={styles.card}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="auth-confirm-title"
        aria-describedby={message ? 'auth-confirm-message' : undefined}
      >
        <button
          type="button"
          className={styles.close}
          onClick={onCancel}
          disabled={isBusy}
          aria-label="Close"
        >
          <X size={16} />
        </button>

        {icon ? (
          <div className={cn(styles.icon, styles[tone])} aria-hidden="true">
            {icon}
          </div>
        ) : null}

        <h2 className={styles.title} id="auth-confirm-title">{title}</h2>
        {message ? (
          <p className={styles.message} id="auth-confirm-message">{message}</p>
        ) : null}

        <div className={styles.actions}>
          <button
            type="button"
            ref={cancelRef}
            className={cn(styles.button, styles.cancel)}
            onClick={onCancel}
            disabled={isBusy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={cn(
              styles.button,
              styles.confirm,
              tone === 'danger' ? styles.confirmDanger : styles.confirmAccent
            )}
            onClick={onConfirm}
            disabled={isBusy}
          >
            {isBusy ? <span className={styles.spinner} /> : null}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
